// Modal infrastructure for Letter Flick — onboarding, endgame, and toast.
//
// Built on the native <dialog> element so we get focus trapping, backdrop
// rendering, and Escape-to-close for free, with zero framework. Dialogs are
// pre-rendered in index.html; this module owns the JS side of show/hide and
// the toast queue.
//
// Depends on the ui/modalReassert leaf primitive: the resume reconciler heals
// the iOS background→resume lockup by close()→showModal()-ing open dialogs, and
// the onboarding + endgame 'close' listeners below must bail during that cycle
// (their teardown self-removes listeners / fires continuations that the
// re-assert must not trigger).

import { isReasserting } from './modalReassert'
//
// Dismissal contract:
//   - Onboarding closes via the <form method="dialog"> submit (either the
//     "Let's Play" or the "Let's Practice" button). We discriminate on the
//     dialog's returnValue ("play" vs "practice") to fire the right handler.
//     Backdrop dismissal is blocked AND the `cancel` event (Escape on first
//     visit or help re-entry) is preventDefault'd — the player MUST choose
//     one of the two CTAs to proceed.
//   - Endgame closes via the explicit X button or Escape key only. NO backdrop
//     click. Reason: avoids "I tapped Share and my finger slipped onto the
//     backdrop, dismissing the modal" on touch devices.
//

export interface OnboardingHandlers {
  // Fired when the player submits the form with "Let's Play". The orchestrator
  // wires this to mountDaily() (first visit also seeds setOnboardedAt).
  readonly onPlay: () => void
  // Fired when the player submits the form with "Let's Practice". The
  // orchestrator wires this to mountPractice(1) — practice puzzles are all
  // unlocked; the player navigates between them via prev/next once mounted.
  readonly onPractice: () => void
  // Fired when the modal is closed without a form submission — the X button
  // is the primary trigger. If omitted, an X-close falls through to onPlay
  // (legacy first-visit behavior: X means "OK go to daily"). Help-button
  // reopens should provide this as a no-op so dismissing the rules-reference
  // view leaves the player on whatever mode they were already in.
  readonly onDismiss?: () => void
}

// Shows the onboarding modal. The `<form method="dialog">` inside the dialog
// fires a `close` event whose `returnValue` is the submitting button's value
// attribute — "play" or "practice". We listen once and dispatch to the right
// handler. Both listeners (close + cancel) self-remove in the close handler
// so reopening the dialog later doesn't double-fire.
//
// `cancel` (fires on Escape and on browser-default backdrop dismissal) is
// preventDefault'd so the player can't accidentally bypass the choice — on
// first visit and on help re-entry alike.
export function showOnboarding(
  dialog: HTMLDialogElement,
  handlers: OnboardingHandlers,
): void {
  const onCancel = (e: Event): void => {
    e.preventDefault()
  }
  const onClose = (): void => {
    // LITERAL FIRST line: bail before the self-removals below when the resume
    // reconciler is mid-re-assert. The re-assert's close() fires this event
    // synchronously; if we ran the removeEventListener calls here, the
    // subsequent showModal() would leave the dialog with NO close/cancel
    // listeners — the next real Escape/X would silently do nothing. Bailing
    // first preserves both real listeners through the re-assert.
    if (isReasserting(dialog)) return
    dialog.removeEventListener('close', onClose)
    dialog.removeEventListener('cancel', onCancel)
    // returnValue is the submitting button's `value` attribute, set when
    // the form submits via "Let's Play" / "Let's Practice". X-button or
    // any non-form close leaves it empty.
    if (dialog.returnValue === 'practice') {
      handlers.onPractice()
    } else if (dialog.returnValue === 'play') {
      handlers.onPlay()
    } else if (handlers.onDismiss !== undefined) {
      handlers.onDismiss()
    } else {
      handlers.onPlay()
    }
  }
  dialog.addEventListener('close', onClose)
  dialog.addEventListener('cancel', onCancel)
  // Defensive: clear any prior returnValue so reopening doesn't carry stale
  // state into the close handler.
  dialog.returnValue = ''
  dialog.showModal()
}

// Programmatic close. Rarely used (the form-submit path is the primary close
// route), but exposed for symmetry and for tests/dev tools.
export function closeOnboarding(dialog: HTMLDialogElement): void {
  if (dialog.open) dialog.close()
}

// ---------- Endgame modal ----------

// A single "next action" button rendered in the endgame modal alongside the
// Share button. Practice mode supplies a Replay button (always) plus either
// "Next practice" (if there's another puzzle to play) or "Back to daily" (on
// the final puzzle). Daily and dev modes omit this and only show Share.
export interface EndgameNextAction {
  readonly label: string
  readonly onClick: () => void
}

export interface EndgameContent {
  readonly title: string
  // Loss-reveal copy ("The word was DESK."). Empty string for a win.
  readonly loseReveal: string
  // Subhead under the title — e.g. "Letter Flick #207" or "Letter Flick #207 X".
  readonly subhead: string
  // The grid rows, one per attempt snapshot (undo rows then final-state row).
  // Each row already includes its terminator(s) — `↩️` for undo rows, `·` for
  // every row — so the modal just needs to wrap each in a <span> for layout.
  // See sharegrid.ts for format.
  readonly gridRows: readonly string[]
  // Screen-reader description of the grid (from sharegrid.ts).
  readonly gridAria: string
  // Tally line — e.g. "5 letters · 6 flicks · 2 undos".
  readonly tally: string
  // Countdown to next puzzle — e.g. "Next Letter Flick in 07:22:09", ticked
  // live by main.ts while the modal is open and flipping to "Next Letter Flick
  // is ready." once the rollover instant passes. Practice mode passes an empty
  // string; the `.modal__countdown:empty` CSS rule collapses the empty paragraph
  // so it doesn't leave a hollow row.
  readonly countdown: string
  // Optional list of "next" actions (Replay, Next practice, Back to daily,
  // etc.). Each renders as a secondary button BEFORE the Share button in the
  // endgame modal's action row. Omit (do not pass undefined under
  // exactOptionalPropertyTypes) when no extra actions are needed; the modal
  // then hides + clears the container.
  readonly nextActions?: readonly EndgameNextAction[]
}

export interface EndgameElements {
  readonly dialog: HTMLDialogElement
  readonly title: HTMLElement
  readonly loseReveal: HTMLElement
  readonly subhead: HTMLElement
  readonly grid: HTMLElement
  readonly tally: HTMLElement
  readonly countdown: HTMLElement
  readonly closeButton: HTMLButtonElement
  readonly shareButton: HTMLButtonElement
  // Container for dynamic "next action" buttons (Replay, Next practice, etc.).
  // Hidden when there are no actions; populated + unhidden when there are.
  readonly nextActionsContainer: HTMLElement
}

export interface EndgameHandlers {
  readonly onClose: () => void
  readonly onShare: () => void
}

// Internal bookkeeping: track the close + share listeners across show/hide
// cycles so we can detach them and avoid leaks if the modal is reopened.
// Module-scoped because there's exactly one endgame modal per page. Next-
// action listeners are tracked in a parallel array because the set of
// next-action buttons changes per `showEndgame` call — we recreate them on
// each show, so we also detach on each show.
let endgameCloseHandler: (() => void) | null = null
let endgameShareHandler: (() => void) | null = null
let endgameCloseListener: (() => void) | null = null
let endgameShareListener: (() => void) | null = null
// Parallel arrays — element[i] gets listener[i] attached on click. We rebuild
// both on every show, so the array length is the source of truth for "how
// many next-action buttons are live right now".
const endgameNextActionButtons: HTMLButtonElement[] = []
const endgameNextActionListeners: Array<() => void> = []

export function showEndgame(
  els: EndgameElements,
  content: EndgameContent,
  handlers: EndgameHandlers,
): void {
  // Update content. textContent prevents any HTML-injection surface from a
  // future content path that pulls user-influenced strings.
  els.title.textContent = content.title
  if (content.loseReveal === '') {
    els.loseReveal.textContent = ''
    els.loseReveal.hidden = true
  } else {
    els.loseReveal.textContent = content.loseReveal
    els.loseReveal.hidden = false
  }
  els.subhead.textContent = content.subhead
  // Render each grid row as its own <span class="modal__grid-row"> so the CSS
  // can lay them out vertically and they wrap independently on narrow viewports.
  // textContent on each span keeps this DOM-injection-safe; rows come from
  // sharegrid.ts which only produces emoji + the U+00B7 dot.
  els.grid.replaceChildren(
    ...content.gridRows.map((row) => {
      const span = document.createElement('span')
      span.className = 'modal__grid-row'
      span.textContent = row
      return span
    }),
  )
  els.grid.setAttribute('aria-label', content.gridAria)
  els.tally.textContent = content.tally
  els.countdown.textContent = content.countdown

  // Attach handlers. Detach any previous instance first — reopening the modal
  // (via the "View results" button) re-passes handlers, and we want exactly
  // one active listener per button.
  detachEndgameListeners(els)
  endgameCloseHandler = handlers.onClose
  endgameShareHandler = handlers.onShare
  // X-button click just closes the dialog; the dialog's `close` event is the
  // single dispatch site for the onClose handler. This gives single-fire
  // semantics for both X and Escape (mirrors onboarding's pattern), and
  // ensures the dialog actually closes when the user clicks X.
  endgameCloseListener = (): void => {
    els.dialog.close()
  }
  endgameShareListener = (): void => {
    if (endgameShareHandler !== null) endgameShareHandler()
  }
  els.closeButton.addEventListener('click', endgameCloseListener)
  els.shareButton.addEventListener('click', endgameShareListener)

  // ---------- Next-action buttons ----------
  // Recreate the container's children on every show. The previous show's
  // listeners are detached by detachEndgameListeners above; safe to discard
  // the previous DOM nodes too.
  els.nextActionsContainer.replaceChildren()
  const nextActions = content.nextActions
  if (nextActions !== undefined && nextActions.length > 0) {
    for (const action of nextActions) {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'btn btn--secondary'
      btn.textContent = action.label
      // Each click first closes the dialog, THEN fires the action. Closing
      // first means the next mount() — which typically the action triggers —
      // operates on a board the player can immediately see, with no stale
      // modal overlaying the board for a frame. The dialog's `close` event
      // also fires onClose (which sets `viewResultsEl.hidden = false`), so
      // an action click leaves the page in the same state as an X-click did
      // before invoking the next mount.
      const listener = (): void => {
        els.dialog.close()
        action.onClick()
      }
      btn.addEventListener('click', listener)
      els.nextActionsContainer.appendChild(btn)
      endgameNextActionButtons.push(btn)
      endgameNextActionListeners.push(listener)
    }
    els.nextActionsContainer.hidden = false
  } else {
    els.nextActionsContainer.hidden = true
  }

  // Native <dialog> Escape-key behavior fires a `cancel` event then a `close`
  // event. We wire a one-shot `close` listener to invoke the close handler
  // whether dismissal came from the X button (via els.dialog.close() above)
  // or Escape.
  const onDialogClose = (): void => {
    // LITERAL FIRST line: bail before the self-removal + onClose fire when the
    // resume reconciler is mid-re-assert. The re-assert's close() dispatches
    // this synchronously; running onClose here would, e.g., reveal the
    // "View results" button and detach this listener, leaving the re-asserted
    // modal without its close handler. Bailing first keeps the endgame modal
    // intact across a resume.
    if (isReasserting(els.dialog)) return
    els.dialog.removeEventListener('close', onDialogClose)
    if (endgameCloseHandler !== null) endgameCloseHandler()
  }
  els.dialog.addEventListener('close', onDialogClose)

  // INTENTIONAL OMISSION: no backdrop-click dismissal. Touch users have
  // accidentally dismissed similar modals by mis-tapping near the Share button.
  // The explicit X + Escape are the only ways out.

  els.dialog.returnValue = ''
  els.dialog.showModal()
}

export function closeEndgame(els: EndgameElements): void {
  if (els.dialog.open) els.dialog.close()
  detachEndgameListeners(els)
}

function detachEndgameListeners(els: EndgameElements): void {
  if (endgameCloseListener !== null) {
    els.closeButton.removeEventListener('click', endgameCloseListener)
  }
  if (endgameShareListener !== null) {
    els.shareButton.removeEventListener('click', endgameShareListener)
  }
  // Detach any previously-attached next-action listeners. Walk both parallel
  // arrays in lockstep; reset both to empty afterward so the next show starts
  // from a clean slate.
  for (let i = 0; i < endgameNextActionButtons.length; i += 1) {
    const btn = endgameNextActionButtons[i]
    const listener = endgameNextActionListeners[i]
    if (btn !== undefined && listener !== undefined) {
      btn.removeEventListener('click', listener)
    }
  }
  endgameNextActionButtons.length = 0
  endgameNextActionListeners.length = 0
  endgameCloseHandler = null
  endgameShareHandler = null
  endgameCloseListener = null
  endgameShareListener = null
}

